/**
 * Which decisions a decision may be recorded as part of.
 *
 * The record form never offered "part of" before 2026-10-07, so no decision
 * anywhere had a parent and the decision map drew every workspace as one
 * vertical line. A parent must stand (active), must not be the decision
 * itself, and must not be one of its own sub-decisions — a loop would make
 * the map's tree walk never end.
 */
import type { Decision } from '@/types/prioritization/decisions';

type Node = Pick<Decision, 'id' | 'parentDecisionId' | 'status' | 'title'>;

/** The ids of every decision under `rootId`, at any depth. */
export function decisionDescendantIds(decisions: readonly Node[], rootId: string): Set<string> {
  const children = new Map<string, string[]>();
  for (const d of decisions) {
    if (d.parentDecisionId) (children.get(d.parentDecisionId) ?? children.set(d.parentDecisionId, []).get(d.parentDecisionId)!).push(d.id);
  }
  const out = new Set<string>();
  const stack = [rootId];
  while (stack.length) {
    const id = stack.pop()!;
    for (const child of children.get(id) ?? []) {
      if (!out.has(child)) { out.add(child); stack.push(child); }
    }
  }
  return out;
}

/** Decisions that may be chosen as `selfId`'s parent (pass null when recording a new one). */
export function parentDecisionCandidates<T extends Node>(decisions: readonly T[], selfId: string | null): T[] {
  const below = selfId ? decisionDescendantIds(decisions, selfId) : new Set<string>();
  return decisions
    .filter((d) => d.status === 'active' && d.id !== selfId && !below.has(d.id))
    .sort((a, b) => a.title.localeCompare(b.title));
}
